/* eslint-disable react/jsx-props-no-spreading */
import * as React from "react";
import { useLayer, useHover, Arrow } from "react-laag";
import { isReactText } from "@utils/react";
import { ROOT_ID } from "src/constants";

const BG_COLOR = "rgb(17 24 39)";
const BORDER_COLOR = "rgb(17 24 39)";

type TooltipProps = React.PropsWithChildren<{
  text: string;
}>;

const Tooltip = ({ children, text }: TooltipProps): JSX.Element => {
  // We use `useHover()` to determine whether we should show the tooltip.
  // Notice how we're configuring a small delay on enter / leave.
  const [isOver, hoverProps] = useHover({ delayEnter: 100, delayLeave: 300 });

  // Tell `useLayer()` how we would like to position our tooltip
  const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
    isOpen: isOver,
    auto: true,
    // placement: "bottom-start",
    triggerOffset: 8, // small gap between wrapped content and the tooltip
    container: ROOT_ID, // id of the root-layer container
  });

  // when children equals text (string | number), we need to wrap it in an
  // extra span-element in order to attach props
  let trigger;
  if (isReactText(children)) {
    trigger = (
      <span className="tooltip-text-wrapper" {...triggerProps} {...hoverProps}>
        {children}
      </span>
    );
  } else if (React.isValidElement(children)) {
    trigger = React.cloneElement(children, {
      ...triggerProps,
      ...hoverProps,
    });
  }

  // We're using framer-motion for our enter / exit animations.
  // This is why we need to wrap our actual tooltip inside `<AnimatePresence />`.
  // The only thing left is to describe which styles we would like to animate.
  return (
    <>
      {trigger}
      {renderLayer(
        isOver && (
          <div
            className="px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-sm"
            {...layerProps}
          >
            {text}
            <Arrow
              {...arrowProps}
              backgroundColor={BG_COLOR}
              borderColor={BORDER_COLOR}
              borderWidth={1}
              size={6}
            />
          </div>
        )
      )}
    </>
  );
};

export default Tooltip;
